import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { connect } from 'react-redux'
import { FormGroup } from 'react-bootstrap'
import { useTrackingTrigger } from '@indeed/flex-tracking-context'

import { Modal } from 'syft-acp-core/components/Modal'
import { getModal$ } from 'syft-acp-core/store/modals/selectors'
import { hideModal } from 'syft-acp-core/store/modals/actions'
import { cancelBookingsForWorker } from 'syft-acp-core/actions/bookings'

import { trackingEvents } from './BulkCancelBookingModal.tracking'
import { ModalBody } from './ModalBody'
import {
  buildCancelPayload,
  buildCoverPreview,
  hasFlaggedShift,
  isOtherReason,
  loadCancellableBookings,
  loadOverbookCover,
  MODAL_NAME,
  reasonLabelFor,
  submitLabel,
  toggleInList,
} from './helpers'
import {
  BulkCancelBookingModalProps,
  CancelReason,
  CancelStep,
  ShiftOverbookCover,
  SpotDecision,
  WorkerJobBooking,
} from './types'
import './index.css'

export const BulkCancelBookingModal = ({ dispatch, item }: BulkCancelBookingModalProps) => {
  const { worker = {}, jobID, currentShiftID, isBlockBooking = false, jobShiftCount } = item.options
  const isShown = item.isShown

  // Step 1 picks the shifts + reason; step 2 is the destructive spot decision and
  // final confirmation — kept separate so it isn't glossed over.
  const [step, setStep] = useState<CancelStep>('select')
  const [bookings, setBookings] = useState<WorkerJobBooking[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [loadError, setLoadError] = useState<string | undefined>(undefined)
  const [selected, setSelected] = useState<number[]>([])
  const [reason, setReason] = useState<CancelReason>(null)
  const [otherReason, setOtherReason] = useState<string | undefined>(undefined)
  // Keyed by shift_id. Shows who's first in line on a selected shift's overbook, if
  // anyone — drives whether "overbook" is offered as a spot decision in step 2, and
  // the per-date breakdown when it's chosen. Never blocks or changes what gets
  // submitted on its own; see STSMPD-1854.
  const [overbookCover, setOverbookCover] = useState<Record<number, ShiftOverbookCover>>({})
  // What happens to the vacated spot — set to a sensible default on entering step 2.
  const [spotDecision, setSpotDecision] = useState<SpotDecision>('keep')
  const triggerEvent = useTrackingTrigger()

  // Resets every choice made in the dialog, leaving the loaded bookings alone.
  const resetDecisions = useCallback(() => {
    setStep('select')
    setReason(null)
    setOtherReason(undefined)
    setSpotDecision('keep')
  }, [])

  // Reruns whenever the target worker/job/shift changes while the modal stays mounted.
  useEffect(
    () =>
      loadCancellableBookings({
        isShown,
        workerID: worker.id,
        jobID,
        currentShiftID,
        resetDecisions,
        setBookings,
        setSelected,
        setLoading,
        setLoadError,
      }),
    [isShown, worker.id, jobID, currentShiftID, resetDecisions],
  )

  // Mirrors flex-core's own promotion gate (CancelBooking#promote_overbook_workers:
  // `return unless job.shifts.one?`). Cover on a multi-shift job would never actually be
  // promoted, so the dialog must not offer it as an outcome — and needn't even look it up.
  const promotionEligible = jobShiftCount === 1

  // Keyed off a joined string rather than the `selected` array reference so toggling
  // doesn't re-fire this on every render.
  const selectedKey = selected.join(',')
  useEffect(
    () => loadOverbookCover({ isShown: isShown && promotionEligible, shiftIDs: selected, setOverbookCover }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isShown, promotionEligible, selectedKey],
  )

  const resetState = useCallback(() => {
    resetDecisions()
    setSelected([])
    setBookings([])
    setLoadError(undefined)
    setOverbookCover({})
  }, [resetDecisions])

  const closeModal = useCallback(() => {
    dispatch(hideModal(item.modalName))
    resetState()
  }, [dispatch, item.modalName, resetState])

  const toggleShift = (id: number) => setSelected(prev => toggleInList(prev, id))

  const allIds = useMemo(() => bookings.map(b => b.shift_id), [bookings])
  const allSelected = selected.length === allIds.length && allIds.length > 0
  const toggleAll = () => setSelected(allSelected ? [] : allIds)

  const reasonLabel = reasonLabelFor(reason)

  const otherValid = isOtherReason(reasonLabel ?? reason) ? !!otherReason : true
  const canGoNext = selected.length > 0 && !!reason && otherValid && !loading

  const selectedHasFlag = useMemo(() => hasFlaggedShift(bookings, selected), [bookings, selected])

  const coverPreview = useMemo(
    () => buildCoverPreview(selected, bookings, overbookCover),
    [selected, bookings, overbookCover],
  )

  // Does at least one selected shift have someone first in line to move in? No listing,
  // or an empty one, means there's nobody to promote — which is what decides whether
  // keeping the spot is described as an overbook fill or as a plain reopen.
  const coveredCount = useMemo(() => coverPreview.filter(({ cover }) => cover.covered).length, [coverPreview])
  const hasOverbookCover = coveredCount > 0

  // Whether an overbook fill was even on offer when a decision was made — without it,
  // "reopen the offering" is ambiguous in analytics: it can mean ops rejected an available
  // overbook, or that there was nothing to reject. `overbook_promotion_eligible` says
  // whether flex-core could have promoted at all (single-shift job), and
  // `overbook_cover_resolved_shifts` separates a genuine "no cover" from an unknown one
  // (cover fetch failed, still in flight, or skipped as ineligible).
  const overbookAvailability = useMemo(
    () => ({
      overbook_available: hasOverbookCover,
      overbook_promotion_eligible: promotionEligible,
      overbook_covered_shifts: coveredCount,
      overbook_cover_resolved_shifts: coverPreview.length,
      selected_shift_count: selected.length,
    }),
    [hasOverbookCover, promotionEligible, coveredCount, coverPreview.length, selected.length],
  )

  // Tracks every spot decision the user actively picks on step 2. The default is set by
  // goToConfirm rather than a click, so accepting it produces no event here — the final
  // choice is always on the save event too.
  const selectSpotDecision = useCallback(
    (decision: SpotDecision) => {
      setSpotDecision(decision)
      triggerEvent(trackingEvents.BULK_CANCEL_BOOKING.SPOT_DECISION.SELECTED, {
        spot_decision: decision,
        ...overbookAvailability,
      })
    },
    [triggerEvent, overbookAvailability],
  )

  const goToConfirm = useCallback(() => {
    setSpotDecision('keep')
    setStep('confirm')
  }, [])

  // A single /bookings/delete call — `delete_spot` tells flex-core whether to reopen the
  // shift (and let it promote an overbook backup) or shrink/remove the spot instead. Must
  // stay one call: doing this as delete-then-remove_spot races the async overbook-
  // promotion reaction the reopen triggers (STSMPD-2072).
  const onConfirm = useCallback(() => {
    const payload = buildCancelPayload({
      workerID: worker.id,
      jobID,
      shiftIDs: selected,
      currentShiftID,
      reason,
      reasonLabel,
      otherReason,
      deleteSpot: spotDecision === 'delete',
    })
    // Exclude the free-text custom reason from analytics — it can contain PII about the
    // worker. Track the structured fields only.
    const { user_provided_custom_reason: _omit, ...trackedPayload } = payload
    triggerEvent(trackingEvents.BULK_CANCEL_BOOKING.SAVE.CLICKED, {
      ...trackedPayload,
      spot_decision: spotDecision,
      ...overbookAvailability,
    })
    dispatch(cancelBookingsForWorker(payload))
    closeModal()
  }, [
    selected,
    reason,
    reasonLabel,
    otherReason,
    spotDecision,
    overbookAvailability,
    worker,
    jobID,
    currentShiftID,
    closeModal,
    dispatch,
    triggerEvent,
  ])

  const count = selected.length

  return (
    <Modal
      className="bulk-cancel-modal"
      header={`Cancel ${worker.first_name || ''} ${worker.last_name || ''}`.trim()}
      isShown={isShown}
      isLoading={loading}
      onClose={closeModal}
      onConfirm={step === 'select' ? goToConfirm : onConfirm}
      canSubmit={step === 'select' ? canGoNext : true}
      confirmKind={step === 'confirm' && spotDecision === 'delete' ? 'danger' : 'primary'}
      confirmationText={submitLabel({
        step,
        count,
        spotDecision,
      })}
    >
      <FormGroup className="form-item">
        <ModalBody
          loadError={loadError}
          loading={loading}
          step={step}
          selectStepProps={{
            worker,
            isBlockBooking,
            bookings,
            allSelected,
            toggleAll,
            selected,
            toggleShift,
            selectedHasFlag,
            reason,
            reasonLabel,
            setReason,
            otherReason,
            setOtherReason,
          }}
          spotDecisionStepProps={{
            count,
            worker,
            reason,
            reasonLabel,
            otherReason,
            spotDecision,
            setSpotDecision: selectSpotDecision,
            hasOverbookCover,
            coverPreview,
            onBack: () => setStep('select'),
          }}
        />
      </FormGroup>
    </Modal>
  )
}

export default connect((state: any) => ({
  item: getModal$(state.modals)(MODAL_NAME),
  // @ts-expect-error getModal$ returns the generic ModalsItem; options is narrowed to
  // BulkCancelModalOptions once this modal is shown (see EndJobAssignmentModal).
}))(BulkCancelBookingModal)
